import axiosInstance from "./AxiosInstance"

const formatDate = (date) => {
  if (!date) return "-"
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const removeNullValues = (obj) => {
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== null && item !== undefined)
      .map(item => removeNullValues(item))
  }
  
  if (obj !== null && typeof obj === 'object') {
    const cleaned = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined) {
        cleaned[key] = removeNullValues(value)
      }
    }
    return cleaned
  }
  
  return obj
}

export function getCasePaperData(patientId) {
  return axiosInstance.post("/patient/case-paper", {
    patient_id: patientId,
  })
}

export function formatCasePaperData(rawData) {
  if (!rawData) return null

  console.log("Raw case paper API response:", rawData)

  const formattedAppointments = []

  if (rawData.timeline) {
    Object.keys(rawData.timeline).forEach((date) => {
      const dayData = rawData.timeline[date]

      // Format reports from dayData - FIXED
      const formattedReports = (dayData.reports || [])
        .filter(report => {
          // Keep reports that have valid report_files array with URLs
          return report.report_files && 
                 Array.isArray(report.report_files) && 
                 report.report_files.some(f => f.report_file_url && f.report_file_url.trim() !== "")
        })
        .map(report => ({
          id: report.report_id || "",
          date: report.report_date || "",
          type: report.report_type || "",
          title: report.report_title || "",
          description: report.report_description || report.description || "",
          fileUrl: report.file_url || "",
          thumbnailUrl: report.thumbnail_url || "",
          uploadedBy: report.uploaded_by || "",
          status: report.report_status || report.status || "",
          // Map report_files array with proper URL validation
          reportFiles: (report.report_files || [])
            .filter(file => file && file.report_file_url && file.report_file_url.trim() !== "")
            .map((file) => ({
              id: file.report_file_id || "",
              fileUrl: file.report_file_url || "",
              fileName: file.report_file || "",
              createdAt: file.created_at || "",
            })),
        }))

      const appointment = {
        id: `appointment_${date}`,
        date: date,
        time: "",
        doctor: "",
        status: "Completed",
        notes: dayData.notes || [],
        prescriptions: dayData.prescriptions || [],
        reports: formattedReports,
        panchakarma: dayData.panchakarma || [],
        counsellor: dayData.counsellor || {},
        medical: dayData.medical || {},
        followup_calls: dayData.followups || [],
        male_chart: dayData.malechart || dayData.male_chart || [],
        menopause: dayData.menopause || [],
        GAPLD: dayData.gapld || dayData.GAPLD || [],
        questionnaires: dayData.questionnaire || dayData.questionnaires || [],
      }

      formattedAppointments.push(appointment)
    })
  }

  const formattedData = {
    patient: {
      id: rawData.patient?.patient_id || "",
      name: rawData.patient?.name || "",
      contact: rawData.patient?.contact || "",
      email: rawData.patient?.email || "",
      profileImage: rawData.patient?.profile_image || "",
      gender: rawData.patient?.gender || "",
      age: rawData.patient?.age || "",
      dob: rawData.patient?.dob || "",
      height: rawData.patient?.height || "",
      weight: rawData.patient?.weight || "",
      occupation: rawData.patient?.occupation || "",
      branch: rawData.patient?.branch || "",
      center: rawData.patient?.center || "",
      additionalContact: rawData.patient?.additional_contact || "",
      marriageHistory: rawData.patient?.marriage_history || "",
      marriageDate: rawData.patient?.marriage_date || "",
      spouseName: rawData.patient?.spouse_name || "",
      spouseOption: rawData.patient?.spouse_option || "",
      diseases: rawData.patient?.diseases || [],
      diseaseStatus: rawData.patient?.disease_status || "",
      currentDisease: rawData.patient?.current_disease || "",
      firstAppointment: rawData.patient?.first_appointment || "",
      firstAppointmentDuration: rawData.patient?.duration_first_appointment || "",
      medicineTaken: rawData.patient?.medicine_taken || "",
      numberOfVisits: rawData.patient?.no_of_visit || "0",
      numberOfMedicinesTaken: rawData.patient?.no_of_medicine_taken || "0",
      latestFollowupDate: formatDate(rawData.patient?.latest_followup_date),
      nextAppointmentDate: formatDate(rawData.patient?.next_appointment_date),
      addresses: {
        temporary: rawData.patient?.temporary_address || {},
        permanent: rawData.patient?.permanent_address || {},
      },
      otherInfo: rawData.patient?.other_info || {},
    },
    offlineVisitCount:
      rawData.OfflineVisitCount ||
      rawData.patient?.OfflineVisitCount ||
      rawData.offlineVisitCount ||
      0,
    onlineVisitCount:
      rawData.OnlineVisitCount ||
      rawData.patient?.OnlineVisitCount ||
      rawData.onlineVisitCount ||
      0,
    appointments: formattedAppointments,

    prescriptions:
      rawData.prescriptions?.map((prescription) => ({
        id: prescription.prescription_id || "",
        date: prescription.prescription_date || "",
        doctor: prescription.doctor_name || "",
        medicines:
          prescription.medicines?.map((medicine) => ({
            id: medicine.medicine_id || "",
            name: medicine.medicine_name || "",
            dose: medicine.dose || "",
            frequency: medicine.frequency || "",
            quantity: medicine.quantity || "",
            deliveredQuantity: medicine.updated_qty || "",
            howToTake: medicine.how_to_take || "",
            status: medicine.status || "",
            duration: medicine.duration || "-",
          })) || [],
        status: prescription.status || "",
      })) || [],

    // Reports - FIXED with proper URL handling
    reports:
      rawData.reports
        ?.filter(report => {
          if (!report.report_id) return false
          const hasNewFiles = report.report_files && 
                             Array.isArray(report.report_files) && 
                             report.report_files.some(f => f.report_file_url && f.report_file_url.trim() !== "")
          return hasNewFiles
        })
        ?.map((report) => ({
          id: report.report_id || "",
          date: report.report_date || "",
          type: report.report_type || "",
          title: report.report_title || "",
          description: report.report_description || report.description || "",
          fileUrl: report.file_url || "",
          thumbnailUrl: report.thumbnail_url || "",
          uploadedBy: report.uploaded_by || "",
          status: report.report_status || report.status || "",
          reportFiles: (report.report_files || [])
            .filter(file => file && file.report_file_url && file.report_file_url.trim() !== "")
            .map((file) => ({
              id: file.report_file_id || "",
              fileUrl: file.report_file_url || "",
              fileName: file.report_file || "",
              createdAt: file.created_at || "",
            })),
        })) || [],

    notes:
      rawData.notes?.map((note) => ({
        id: note.note_id || "",
        title: note.note_title || "",
        description: note.note_description || "",
        type: note.note_type || "",
        createdBy: note.created_by_name || "",
        createdAt: note.created_at || "",
        updatedAt: note.updated_at || "",
        status: note.status || "",
      })) || [],

    latestNotes: rawData.latestNotes || rawData.latest_notes || rawData.notes || [],
    ImportantQuestions: rawData.ImportantQuestions || rawData.important_questions || [],

    panchakarma:
      rawData.panchakarma?.map((treatment) => ({
        id: treatment.panchakarma_id || "",
        name: treatment.panchakarma_name || "",
        startDate: treatment.start_date || "",
        endDate: treatment.end_date || "",
        duration: treatment.duration || "",
        status: treatment.status || "",
        doctor: treatment.doctor_name || "",
        notes: treatment.notes || "",
        recommendations: treatment.recommendations || [],
      })) || [],

    counsellor:
      rawData.counsellor?.map((session) => ({
        id: session.counsellor_id || "",
        date: session.session_date || "",
        counsellorName: session.counsellor_name || "",
        medicineExplainedDate: session.medicine_explained_date || "",
        medicineExplainedBy: session.medicine_explained_by || "",
        notes: session.notes || "",
        recommendations: session.recommendations || [],
      })) || [],

    medical:
      rawData.medical?.map((record) => ({
        id: record.medical_id || "",
        date: record.record_date || "",
        type: record.record_type || "",
        preparedBy: record.prepared_by || "",
        sentDate: record.sent_date || "",
        deliveryDate: record.delivery_date || "",
        deliveryMethod: record.delivery_method || "",
        status: record.status || "",
        notes: record.notes || "",
      })) || [],

    followupCalls:
      rawData.followup_calls?.map((call) => ({
        id: call.followup_id || "",
        date: call.followup_date || "",
        caller: call.caller_name || "",
        status: call.status || "",
        notes: call.notes || "",
        nextFollowupDate: call.next_followup_date || "",
        actionRequired: call.action_required || "",
      })) || [],

    questionnaires:
      rawData.questionnaires?.map((questionnaire) => ({
        id: questionnaire.questionnaire_id || "",
        date: questionnaire.questionnaire_date || "",
        type: questionnaire.questionnaire_type || "",
        questions:
          questionnaire.questions?.map((q) => ({
            id: q.question_id || "",
            question: q.question_text || "",
            answer: q.answer || "",
            category: q.category || "",
          })) || [],
        status: questionnaire.status || "",
      })) || [],

    maleChart: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.male_chart && appointment.male_chart.length > 0) {
        appointment.male_chart.forEach((chart) => {
          acc.push({
            id: chart.male_chart_id || "",
            patientId: chart.patient_id || "",
            date: chart.malechart_date || chart.date || "",
            symptoms: chart.symptoms_intercourse || chart.symptoms || "",
            metallicColor: chart.metalic_color || chart.metallic_color || "",
            status: chart.male_chart_status || chart.status || "",
            createdBy: chart.created_by || "",
            createdAt: chart.created_at || "",
            updatedAt: chart.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    menopause: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.menopause && appointment.menopause.length > 0) {
        appointment.menopause.forEach((meno) => {
          acc.push({
            id: meno.menopause_id || "",
            patientId: meno.patient_id || "",
            menopauseDate: meno.menopause_date || "",
            ageFirstMenstruation: meno.age_first_m || "",
            menstruation: meno.menstruation || "",
            cycleDays: meno.how_many_days_occur || "",
            stoppedSince: meno.how_long_m_stopped || "",
            causes: meno.causes_m || "",
            otherNotes: meno.other_name || "",
            status: meno.menopause_status || "",
            createdAt: meno.created_at || "",
            createdBy: meno.created_by || "",
            updatedAt: meno.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    GAPLD: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.GAPLD && appointment.GAPLD.length > 0) {
        appointment.GAPLD.forEach((gapld) => {
          acc.push({
            id: gapld.gapld_id || "",
            patientId: gapld.patient_id || "",
            pregnancyStatus: gapld.pregnancy_status || "",
            deliveryDate: gapld.delivery_date || "",
            howWasDelivery: gapld.How_was_the_delivery || "",
            gender: gapld.gender || "",
            abortionDate: gapld.abortion_date || "",
            whichRxHelped: gapld.which_rx_helped || "",
            howDidMiscarriageHappen: gapld.how_did_miscarriage_happen || "",
            miscarriageReason: gapld.miscarriage_reason || "",
            gapldStatus: gapld.gapld_status || "",
            marriageInfoId: gapld.marriage_info_id || "",
            marriageHistory: gapld.marriage_history || "",
            createdBy: gapld.created_by || "",
            createdAt: gapld.created_at || "",
            updatedAt: gapld.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    statistics: {
      totalAppointments: rawData.statistics?.total_appointments || 0,
      totalPrescriptions: rawData.statistics?.total_prescriptions || 0,
      totalReports: rawData.statistics?.total_reports || 0,
      totalNotes: rawData.statistics?.total_notes || 0,
      totalPanchakarma: rawData.statistics?.total_panchakarma || 0,
      totalFollowupCalls: rawData.statistics?.total_followup_calls || 0,
      doctorNoteCount: rawData.statistics?.doctor_note_count || 0,
      telecallerNoteCount: rawData.statistics?.telecaller_note_count || 0,
      followupCallCount: rawData.statistics?.followup_call_count || 0,
    },

    platform: {
      source: rawData.platform?.source || "",
      campaign: rawData.platform?.campaign || "",
      adSet: rawData.platform?.ad_set || "",
      leadSource: rawData.platform?.lead_source || "",
      status: rawData.platform?.status || "",
      priority: rawData.platform?.priority || "",
    },

    lastUpdated: rawData.last_updated || new Date().toISOString(),
  }

  const cleanedData = removeNullValues(formattedData)
  return cleanedData
}

export function getFormattedCasePaperData(patientId) {
  return getCasePaperData(patientId)
    .then((response) => {
      if (response?.status === 200) {
        return response.data
      } else {
        throw new Error(response?.data?.message || "Failed to fetch case paper data")
      }
    })
    .catch((error) => {
      console.error("Error getting formatted case paper data:", error)
      throw error
    })
}
